import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, Select, Spin, Typography, Space } from 'antd';
import { message } from '../services/toastMessage';
import { useTranslation } from 'react-i18next';
import { IconClockOff } from '@tabler/icons-react';
import dayjs, { Dayjs } from 'dayjs';
import { getAvailabilityRange } from '../services/AvailabilityService';
import { IDayAvailability } from '../interfaces/heavyDutyMachinery/IAvailability';
import { BookingRentalMode } from '../interfaces/heavyDutyMachinery/IBooking';
import EmptyState from './EmptyState';
import HourlyRangeTimeline from './HourlyRangeTimeline';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { formatFullDate } from '../helpers/dateFormatHelper';
import { countRentalDaysInclusive, exclusiveEndOfDailyRange } from '../helpers/rentalPeriodHelper';
import { TEAL, TEAL_SOFT_BG, SURFACE_1 } from '../theme/creamyTokens';

const { Text } = Typography;

const MUTED_TEXT = '#adadad';

/** A single day cell -- its own component (rather than a plain function)
 * purely so it can hold the hover-state that drives the soft surface-1
 * highlight on available days; no selection/drag logic lives here, that
 * all still comes from AvailabilityCalendar's cellRender below. */
const CalendarCell: React.FC<{
  day: number;
  selected: boolean;
  inRange: boolean;
  disabled: boolean;
  blocked: boolean;
  outOfRange: boolean;
  interactive: boolean;
  onCellMouseDown?: () => void;
  onCellMouseEnter?: () => void;
}> = ({ day, selected, inRange, disabled, blocked, outOfRange, interactive, onCellMouseDown, onCellMouseEnter }) => {
  const [hovering, setHovering] = useState(false);
  const showHover = hovering && !disabled && !selected && !inRange;
  return (
    <div
      onMouseDown={interactive ? onCellMouseDown : undefined}
      onMouseEnter={() => {
        onCellMouseEnter?.();
        setHovering(true);
      }}
      onMouseLeave={() => setHovering(false)}
      style={{
        textAlign: 'center',
        borderRadius: 14,
        margin: '0 2px',
        padding: '4px 0',
        background: selected ? TEAL : inRange ? TEAL_SOFT_BG : showHover ? SURFACE_1 : undefined,
        color: selected ? '#fff' : outOfRange || blocked ? MUTED_TEXT : undefined,
        textDecoration: blocked ? 'line-through' : undefined,
        textDecorationThickness: blocked ? '1px' : undefined,
        opacity: outOfRange ? 0.55 : 1,
        transform: selected ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 200ms ease, background-color 200ms ease',
        cursor: interactive && !disabled ? 'pointer' : undefined,
        userSelect: interactive ? 'none' : undefined,
      }}
    >
      {day}
    </div>
  );
};

const DATE_FORMAT = 'YYYY-MM-DD';

// Deliberately NOT .toISOString() for the two Overnight check-in/check-out
// startAt/endAt below -- same reasoning as HourlyRangeTimeline's own copy of
// this: that method converts through the BROWSER's own timezone into a real
// UTC instant, which silently shifts a plain calendar-day boundary onto the
// wrong UTC day whenever the guest isn't in the space's own timezone. The
// backend already treats an offset-less string as the space's own local
// wall clock (see AvailabilityCalculator's doc comment), which a midnight
// boundary trivially is regardless of what zone anyone's in.
const toSpaceLocalIso = (d: Dayjs) => d.format('YYYY-MM-DDTHH:mm:ss');

export interface IBookingRange {
  startAt: string; // ISO datetime
  endAt: string; // ISO datetime
}

interface AvailabilityCalendarProps {
  // Optional -- omitted by callers that don't have a specific space yet
  // (e.g. the homepage search bar's own "When" picker, filtering results
  // rather than booking one particular space). Every day is then treated
  // as fully open all day (see getDay below) instead of fetching/reflecting
  // any real host's schedule.
  spaceId?: string;
  mode: BookingRentalMode;
  maxAdvanceBookingDays: number;
  minDurationMinutes: number;
  maxDurationMinutes?: number;
  bufferMinutes: number;
  // Both forwarded straight to HourlyRangeTimeline -- see that component's
  // own props for what each does; optional there and here alike so a caller
  // that only cares about the day-level max-advance-days effect (already
  // handled above, at the month-grid level) doesn't have to pass them.
  minAdvanceBookingHours?: number;
  slotGranularityMinutes?: number;
  pricePerHour?: number;
  // Forwarded straight to HourlyRangeTimeline's own currencyCode -- see that
  // component's own comment. Omitted by the same no-specific-space callers
  // that omit spaceId/pricePerHour.
  currencyCode?: string;
  // Forwarded straight to HourlyRangeTimeline's own barHeight -- see that
  // component's comment; left unset keeps its original embedded-widget size.
  timelineHeight?: number;
  onSelectionChange: (range: IBookingRange | undefined) => void;
}

/**
 * Airbnb-style monthly availability calendar for the guest-facing booking
 * widget. Consumes the batched GET /availability/range endpoint (same
 * OpenWindows/BusyWindows shape as the single-day endpoint) and never
 * computes availability itself -- it only renders what the backend already
 * calculated. Behaviour branches on rental mode:
 * - Hourly: pick a day, then drag a free-form time range on that day's
 *   timeline (HourlyRangeTimeline) -- already-booked/buffer time stays
 *   visible but not draggable into.
 * - Overnight (shown to guests as "por días"): pick the first day, then the
 *   last one -- both INSIDE the rental, so picking the 17th and the 18th is
 *   two days, and clicking the first day twice rents just that one day. The
 *   range is highlighted directly on the same month grid. See
 *   rentalPeriodHelper for why this counts days occupied rather than the
 *   nights-crossed reading this calendar was originally built with.
 * - DayPass: pick a single day; the whole day's open window is booked.
 */
const AvailabilityCalendar: React.FC<AvailabilityCalendarProps> = ({
  spaceId,
  mode,
  maxAdvanceBookingDays,
  minDurationMinutes,
  maxDurationMinutes,
  bufferMinutes,
  minAdvanceBookingHours,
  slotGranularityMinutes,
  pricePerHour,
  currencyCode,
  timelineHeight,
  onSelectionChange,
}) => {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [daysByDate, setDaysByDate] = useState<Record<string, IDayAvailability>>({});
  const [loading, setLoading] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Dayjs | undefined>(undefined);
  // First/last day the guest actually rents -- both INSIDE the range, unlike
  // the check-in/check-out pair these replaced (see rentalPeriodHelper).
  const [firstDay, setFirstDay] = useState<Dayjs | undefined>(undefined);
  const [lastDay, setLastDay] = useState<Dayjs | undefined>(undefined);

  // Overnight's check-in/check-out is driven entirely by our own mouse
  // handlers on each day cell (below), not by antd Calendar's onSelect --
  // that callback turned out to fire unreliably across two separate clicks
  // (sometimes silently resetting to check-in instead of accepting the
  // second click as check-out). dragAnchor is a ref (not state) since it
  // only needs to be read at mouseenter/mouseup time, never trigger a
  // render by itself -- previewEnd is the one that drives the live
  // highlight while dragging.
  const dragAnchorRef = useRef<Dayjs | undefined>(undefined);
  const [previewEnd, setPreviewEnd] = useState<Dayjs | undefined>(undefined);

  const isOvernight = mode === 'Overnight';
  const isHourly = mode === 'Hourly';

  useEffect(() => {
    if (!spaceId) return;
    const from = month.startOf('month').subtract(7, 'day');
    const to = month.endOf('month').add(7, 'day');
    setLoading(true);
    getAvailabilityRange(spaceId, from.format(DATE_FORMAT), to.format(DATE_FORMAT))
      .then((days) => {
        const map: Record<string, IDayAvailability> = {};
        days.forEach((day) => {
          map[day.date] = day;
        });
        setDaysByDate((prev) => ({ ...prev, ...map }));
      })
      .finally(() => setLoading(false));
  }, [spaceId, month]);

  // Without a spaceId there's no real host schedule to fetch, so every day
  // is synthetically "open all day, nothing booked" instead -- see the
  // interface's own comment on why (generic date search vs. booking one
  // specific space).
  const getDay = (date: Dayjs): IDayAvailability | undefined =>
    spaceId
      ? daysByDate[date.format(DATE_FORMAT)]
      : {
          date: date.format(DATE_FORMAT),
          openWindows: [{ start: date.startOf('day').toISOString(), end: date.endOf('day').toISOString() }],
          busyWindows: [],
        };

  // HourlyRangeTimeline resets its in-progress selection whenever the `day`
  // object it's given changes *by reference* (see that component's own
  // `[day]` effect) -- without a spaceId, getDay() above builds a brand new
  // object literal on every call, so calling it inline in render gave the
  // timeline a fresh reference on every unrelated re-render of this
  // component (e.g. the one onConfirm's own callback chain triggers one
  // level up), wiping the selection the instant it was confirmed. Memoized
  // here so the reference only actually changes when the day it represents
  // does.
  const singleDay = useMemo(
    () => (selectedDate ? getDay(selectedDate) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDate, spaceId, daysByDate],
  );

  // The timeline itself gets the selected day's OpenWindows/BusyWindows
  // concatenated with the NEXT day's -- so a guest can drag a selection past
  // midnight (e.g. 23:30 -> 01:30) as one continuous range on a single wider
  // bar, instead of the old hard 24h cutoff. Just concatenating the two
  // days' windows is enough: HourlyRangeTimeline's own minute math
  // (`minutesSinceMidnight`, anchored at the SELECTED day's midnight) already
  // yields >1440 for anything landing on the next calendar day, with no
  // special-casing needed on either side. The single (non-merged) day above
  // is kept separately for the "no slots this day" empty-state check and the
  // date heading, which should still only ever be about the day actually
  // picked, not tomorrow's.
  const timelineDay = useMemo<IDayAvailability | undefined>(() => {
    if (!selectedDate || !singleDay) return singleDay;
    const nextDay = getDay(selectedDate.add(1, 'day'));
    if (!nextDay) return singleDay;
    return {
      date: singleDay.date,
      openWindows: [...singleDay.openWindows, ...nextDay.openWindows],
      busyWindows: [...singleDay.busyWindows, ...nextDay.busyWindows],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, singleDay, spaceId, daysByDate]);

  // Reset any in-progress selection whenever the rental mode changes (e.g.
  // the guest switches from "Por horas" to "Pernocta").
  useEffect(() => {
    setSelectedDate(undefined);
    setFirstDay(undefined);
    setLastDay(undefined);
    onSelectionChange(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const minSelectableDate = dayjs().startOf('day');
  const maxSelectableDate = minSelectableDate.add(maxAdvanceBookingDays, 'day');

  const isOutOfRange = (date: Dayjs) => date.isBefore(minSelectableDate, 'day') || date.isAfter(maxSelectableDate, 'day');

  // A by-days booking takes the machine off site for the WHOLE day, so any
  // existing booking on a day blocks that day entirely -- nobody else can
  // have the excavator for the afternoon. Hourly/DayPass only cares whether
  // the day has any open window at all -- individual busy slots are handled
  // below the calendar instead.
  const isDayBlocked = (date: Dayjs) => {
    const day = getDay(date);
    if (!day) return false;
    if (day.openWindows.length === 0) return true;
    return isOvernight && day.busyWindows.length > 0;
  };

  const isDisabled = (date: Dayjs) => isOutOfRange(date) || isDayBlocked(date);

  // Inclusive of `end`: under the days-not-nights reading (see
  // rentalPeriodHelper) the last day picked is a day the guest actually has
  // the machine, so it has to be free too. While this was exclusive, a range
  // could be committed whose final day was already booked by somebody else.
  const allDaysSelectable = (start: Dayjs, end: Dayjs) => {
    for (let cursor = start; !cursor.isAfter(end, 'day'); cursor = cursor.add(1, 'day')) {
      if (isDisabled(cursor)) return false;
    }
    return true;
  };

  const handleSelect = (date: Dayjs) => {
    if (isDisabled(date)) return;

    setSelectedDate(date);
    onSelectionChange(undefined);

    if (mode === 'DayPass') {
      const day = getDay(date);
      if (day && day.openWindows.length > 0) {
        onSelectionChange({
          startAt: day.openWindows[0].start,
          endAt: day.openWindows[day.openWindows.length - 1].end,
        });
      }
    }
  };

  // Handles both interaction styles in one gesture: a plain click (mouse
  // down and up on the same day, `a` equals `b`) keeps the classic
  // click-first-day-then-click-last-day flow; an actual drag (`a` != `b`)
  // commits the dragged range directly, in either direction.
  const commitRange = (first: Dayjs, last: Dayjs) => {
    setFirstDay(first);
    setLastDay(last);
    onSelectionChange({
      startAt: toSpaceLocalIso(first.startOf('day')),
      // Exclusive: midnight opening the day AFTER the last rented one, so a
      // 17th-to-18th pick really is two days long. See rentalPeriodHelper.
      endAt: toSpaceLocalIso(exclusiveEndOfDailyRange(last)),
    });
  };

  const finalizeOvernightSelection = (a: Dayjs, b: Dayjs) => {
    if (a.isSame(b, 'day')) {
      // Clicking the already-picked first day again commits a single-day
      // rental. Needed now that days are counted inclusively: one day used
      // to be expressed as "the 17th to the 18th", which now means two.
      if (firstDay && !lastDay && a.isSame(firstDay, 'day')) {
        commitRange(firstDay, firstDay);
        return;
      }
      if (firstDay && !lastDay && a.isAfter(firstDay, 'day')) {
        if (allDaysSelectable(firstDay, a)) {
          commitRange(firstDay, a);
          return;
        }
        // A later day was clearly intended as the last one (it's after the
        // existing first day) but some day in between is already booked
        // or closed -- say so instead of silently restarting the selection,
        // which otherwise looks like the click was simply ignored.
        message.warning(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OVERNIGHT_RANGE_UNAVAILABLE));
      }
      setFirstDay(a);
      setLastDay(undefined);
      onSelectionChange(undefined);
      return;
    }

    const start = a.isBefore(b, 'day') ? a : b;
    const end = a.isBefore(b, 'day') ? b : a;
    if (allDaysSelectable(start, end)) {
      commitRange(start, end);
    } else {
      message.warning(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_OVERNIGHT_RANGE_UNAVAILABLE));
      setFirstDay(start);
      setLastDay(undefined);
      onSelectionChange(undefined);
    }
  };

  const handleCellMouseDown = (date: Dayjs) => {
    if (isDisabled(date)) return;
    dragAnchorRef.current = date;
    setPreviewEnd(date);
  };

  const handleCellMouseEnter = (date: Dayjs) => {
    if (!dragAnchorRef.current || isDisabled(date)) return;
    setPreviewEnd(date);
  };

  // Window-level, not per-cell, so the drag still resolves correctly even
  // if the mouse is released outside any day cell.
  useEffect(() => {
    if (!isOvernight) return undefined;
    const handleWindowMouseUp = () => {
      const anchor = dragAnchorRef.current;
      if (!anchor) return;
      finalizeOvernightSelection(anchor, previewEnd ?? anchor);
      dragAnchorRef.current = undefined;
      setPreviewEnd(undefined);
    };
    window.addEventListener('mouseup', handleWindowMouseUp);
    return () => window.removeEventListener('mouseup', handleWindowMouseUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOvernight, previewEnd, firstDay, lastDay, daysByDate]);

  const cellRender = (date: Dayjs) => {
    const outOfRange = isOutOfRange(date);
    const blocked = !outOfRange && isDayBlocked(date);
    const disabled = outOfRange || blocked;

    let selected = false;
    let inRange = false;
    if (isOvernight) {
      const anchor = dragAnchorRef.current;
      if (anchor) {
        // Actively dragging -- the live anchor/previewEnd pair overrides
        // the last committed firstDay/lastDay until the drag is released.
        const previewStart = previewEnd && previewEnd.isBefore(anchor, 'day') ? previewEnd : anchor;
        const previewFinish = previewEnd && previewEnd.isBefore(anchor, 'day') ? anchor : previewEnd;
        selected = date.isSame(anchor, 'day') || (!!previewEnd && date.isSame(previewEnd, 'day'));
        inRange = !!previewFinish && date.isAfter(previewStart, 'day') && date.isBefore(previewFinish, 'day');
      } else {
        selected = (!!firstDay && date.isSame(firstDay, 'day')) || (!!lastDay && date.isSame(lastDay, 'day'));
        inRange = !!firstDay && !!lastDay && date.isAfter(firstDay, 'day') && date.isBefore(lastDay, 'day');
      }
    } else {
      selected = !!selectedDate && date.isSame(selectedDate, 'day');
    }

    return (
      <CalendarCell
        day={date.date()}
        selected={selected}
        inRange={inRange}
        disabled={disabled}
        blocked={blocked}
        outOfRange={outOfRange}
        interactive={isOvernight}
        onCellMouseDown={isOvernight ? () => handleCellMouseDown(date) : undefined}
        onCellMouseEnter={isOvernight ? () => handleCellMouseEnter(date) : undefined}
      />
    );
  };

  const renderHourlyTimeline = () => {
    if (!selectedDate) return null;
    if (!singleDay) return <Spin size="small" />;
    if (singleDay.openWindows.length === 0)
      return (
        <EmptyState
          icon={<IconClockOff size={24} />}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_NO_SLOTS)}
          description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_NO_SLOTS_DESCRIPTION)}
        />
      );
    const day = timelineDay ?? singleDay;

    return (
      <div>
        <Text strong>{formatFullDate(selectedDate, i18n.language)}</Text>
        <HourlyRangeTimeline
          key={selectedDate.format(DATE_FORMAT)}
          day={day}
          date={selectedDate}
          bufferMinutes={bufferMinutes}
          minDurationMinutes={minDurationMinutes}
          maxDurationMinutes={maxDurationMinutes}
          minAdvanceBookingHours={minAdvanceBookingHours}
          slotGranularityMinutes={slotGranularityMinutes}
          pricePerHour={pricePerHour}
          currencyCode={currencyCode}
          barHeight={timelineHeight}
          onDraftChange={() => onSelectionChange(undefined)}
          onConfirm={(range) => onSelectionChange(range)}
        />
      </div>
    );
  };

  const renderOvernightSummary = () => {
    if (!firstDay) {
      return <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SELECT_CHECKIN_PROMPT)}</Text>;
    }
    if (!lastDay) {
      return (
        <Space direction="vertical" size={0}>
          <Text>
            {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_FIRST_DAY_LABEL)}: {firstDay.format('DD/MM/YYYY')}
          </Text>
          <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_SELECT_CHECKOUT_PROMPT)}</Text>
        </Space>
      );
    }
    // Inclusive: both the first and the last day are days the guest has the
    // machine, so the 17th-to-the-18th is 2 days. See rentalPeriodHelper.
    const days = countRentalDaysInclusive(firstDay, lastDay);
    return (
      <Space direction="vertical" size={0}>
        <Text>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_FIRST_DAY_LABEL)}: {firstDay.format('DD/MM/YYYY')}
        </Text>
        <Text>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LAST_DAY_LABEL)}: {lastDay.format('DD/MM/YYYY')}
        </Text>
        <Text strong>
          {days} {translate(t, days === 1 ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAY_SUFFIX : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_DAYS_SUFFIX)}
        </Text>
      </Space>
    );
  };

  // Bounds the year/month pickers below to whatever's actually bookable --
  // reuses the same minSelectableDate/maxSelectableDate every cell is
  // already checked against, so the dropdowns can't offer a month with
  // nothing selectable in it at all.
  const minMonth = minSelectableDate.startOf('month');
  const maxMonth = maxSelectableDate.startOf('month');
  const yearOptions: number[] = [];
  for (let y = minMonth.year(); y <= maxMonth.year(); y++) yearOptions.push(y);

  return (
    <div>
      {loading && <Spin size="small" style={{ marginBottom: 8 }} />}
      <Calendar
        fullscreen={false}
        value={month}
        onPanelChange={(value) => setMonth(value)}
        disabledDate={isDisabled}
        onSelect={(date, info) => {
          if (info.source !== 'date') return;
          // Overnight's check-in/check-out is driven entirely by the cell
          // divs' own mouse handlers (click and drag) -- see cellRender.
          if (isOvernight) return;
          handleSelect(date);
        }}
        fullCellRender={(date) => cellRender(date)}
        // Replaces antd's own default header entirely -- that default
        // includes a Month/Year view switch on top of the year/month
        // dropdowns. This is a single-day booking picker, not a general
        // calendar, so a year overview has no sensible content to show at
        // all: cellRender above renders one specific day's availability,
        // and antd's year panel calls it once per MONTH instead of once
        // per day, which is how switching to "Year" was showing the same
        // day-of-month number repeated across every month instead of
        // anything meaningful. Only ever rendering our own year/month
        // dropdowns here (never calling onTypeChange) means the Calendar's
        // body can't enter that year panel at all, not just hides the
        // button that used to reach it.
        headerRender={({ value, onChange }) => {
          const year = value.year();
          const monthStart = year === minMonth.year() ? minMonth.month() : 0;
          const monthEnd = year === maxMonth.year() ? maxMonth.month() : 11;
          const monthOptions: number[] = [];
          for (let m = monthStart; m <= monthEnd; m++) monthOptions.push(m);
          return (
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <Select
                size="small"
                value={year}
                style={{ width: 90 }}
                onChange={(y) => onChange(value.clone().year(y))}
                options={yearOptions.map((y) => ({ value: y, label: y }))}
              />
              <Select
                size="small"
                value={value.month()}
                style={{ width: 90 }}
                onChange={(m) => onChange(value.clone().month(m))}
                options={monthOptions.map((m) => ({ value: m, label: dayjs().month(m).format('MMM') }))}
              />
            </div>
          );
        }}
      />
      <div style={{ display: 'flex', gap: 16, marginTop: 4, marginBottom: 12 }}>
        <Space size={4}>
          <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 3, background: TEAL }} />
          <Text type="secondary">{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LEGEND_SELECTED)}</Text>
        </Space>
        <Text type="secondary" style={{ textDecoration: 'line-through', textDecorationThickness: '1px', color: MUTED_TEXT }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LEGEND_UNAVAILABLE)}
        </Text>
      </div>

      {isOvernight ? renderOvernightSummary() : isHourly ? renderHourlyTimeline() : null}
    </div>
  );
};

export default AvailabilityCalendar;
